import {
  Badge, Card, DataTable, ErrorState, Grid, LoadingState, Notice, Stat, Stack,
  useApi, useI18n,
  type PaymentResponse,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';

/**
 * A child's fees, as the client's own ledger had them (§4.8.4).
 *
 * Figures at the top, then the full history as a table of date, amount and status. That is the layout the
 * brief described, and the one a parent can check against their own records. Where the nursery has set this
 * year's total (Q44), the figures are that total, what has been paid towards it, what remains, and the date it
 * is due by; otherwise they are this period's fee, as before.
 *
 * "المتبقي" is the unpaid balance of *issued* invoices and nothing else (FR-FIN-13). The API computes it;
 * this screen never adds anything up on its own, because two places that compute a balance are two places
 * that can disagree about it.
 */
export function Payments({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatMoney, formatDate } = useI18n();

  const account = useQuery({
    queryKey: ['account', childId],
    queryFn: () => api.finance.account(childId),
  });

  if (account.isPending) return <LoadingState />;
  if (account.isError) return <ErrorState error={account.error} onRetry={() => void account.refetch()} />;

  const data = account.data;
  const year = data.yearFee;

  return (
    <Stack>
      {year ? (
        <Grid>
          <Stat
            label={t('payments.yearTotal', { year: year.academicYearName })}
            value={formatMoney(year.totalMinorUnits, data.currency)}
          />
          <Stat label={t('payments.paid')} value={formatMoney(year.paidMinorUnits, data.currency)} />
          <Stat
            label={t('payments.remaining')}
            value={formatMoney(year.remainingMinorUnits, data.currency)}
            note={year.remainingMinorUnits > 0
              ? t('payments.deadline', { date: formatDate(year.deadline) })
              : t('status.Paid')}
            tone={year.remainingMinorUnits === 0 ? 'success' : year.daysLeft < 0 ? 'danger' : 'warning'}
          />
        </Grid>
      ) : (
        <Grid>
          <Stat
            label={t('payments.currentFee')}
            value={formatMoney(data.currentPeriodFeeMinorUnits, data.currency)}
            note={data.feePlanName}
          />
          <Stat
            label={t('payments.paid')}
            value={formatMoney(data.currentPeriodPaidMinorUnits, data.currency)}
          />
          <Stat
            label={t('payments.remaining')}
            value={formatMoney(data.outstandingMinorUnits, data.currency)}
            note={data.outstandingMinorUnits > 0 ? t('status.Overdue') : t('status.Paid')}
            tone={data.outstandingMinorUnits > 0 ? 'warning' : 'success'}
          />
        </Grid>
      )}

      {/* Money paid in advance. Shown separately rather than netted off, so a family can see that the
          nursery is holding it rather than wondering where it went. */}
      {data.creditMinorUnits > 0 && (
        <Notice tone="success" title={t('payments.credit')}>
          {formatMoney(data.creditMinorUnits, data.currency)}
        </Notice>
      )}

      {data.lastPaymentAt && data.lastPaymentMinorUnits != null && (
        <Notice tone="info">
          {t('payments.lastPayment', {
            amount: formatMoney(data.lastPaymentMinorUnits, data.currency),
            date: formatDate(data.lastPaymentAt),
          })}
        </Notice>
      )}

      <Card title={t('invoices.title')} flush>
        <DataTable
          caption={t('invoices.title')}
          rows={data.invoices}
          rowKey={(invoice) => invoice.id}
          empty={t('invoices.empty')}
          columns={[
            { key: 'number', header: t('invoices.number'), render: (i) => i.number },
            {
              key: 'period',
              header: t('invoices.period'),
              render: (i) => `${formatDate(i.periodStart)} – ${formatDate(i.periodEnd)}`,
            },
            { key: 'due', header: t('invoices.dueDate'), render: (i) => formatDate(i.dueDate) },
            {
              key: 'total',
              header: t('invoices.total'),
              numeric: true,
              render: (i) => formatMoney(i.totalMinorUnits, i.currency),
            },
            {
              key: 'outstanding',
              header: t('invoices.outstanding'),
              numeric: true,
              render: (i) => formatMoney(i.outstandingMinorUnits, i.currency),
            },
            {
              key: 'status',
              header: t('audit.action'),
              render: (i) => (
                <Badge tone={i.status === 'Paid' ? 'success' : i.status === 'Overdue' ? 'danger' : 'info'}>
                  {t(`status.${i.status}`)}
                </Badge>
              ),
            },
          ]}
        />
      </Card>

      <Card title={t('payments.history')} flush>
        <DataTable
          caption={t('payments.history')}
          rows={data.payments}
          rowKey={(payment) => payment.id}
          empty={t('payments.noPayments')}
          columns={[
            { key: 'date', header: t('payments.date'), render: (p) => formatDate(p.paidAt) },
            {
              key: 'amount',
              header: t('payments.amount'),
              numeric: true,
              render: (p) => formatMoney(p.amountMinorUnits, p.currency),
            },
            { key: 'method', header: t('payments.method'), render: (p) => t(`payments.method.${p.method}`) },
            { key: 'status', header: t('audit.action'), render: statusBadge },
          ]}
        />
      </Card>
    </Stack>
  );

  /** A voided payment stays on the history, marked. A missing row is a row somebody will ask about. */
  function statusBadge(payment: PaymentResponse) {
    return payment.voidedAt
      ? <Badge tone="neutral">{t('status.Void')}</Badge>
      : <Badge tone="success">{t('status.Paid')}</Badge>;
  }
}
